import { useState, useRef, useEffect, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Input,
  Button,
  Text,
  Spinner,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Badge,
  Tooltip,
} from '@fluentui/react-components';
import {
  SendFilled,
  AddFilled,
  HistoryFilled,
  DismissFilled,
  ChevronLeftRegular,
  ChevronRightRegular,
  ChatRegular,
  BotSparkleRegular,
} from '@fluentui/react-icons';
import { ChatMessage, type ChatMessageData } from './ChatMessage';
import { useAgentChat } from '@/hooks/useAgentChat';
import { formatDate } from '@/utils/dateTime';

/**
 * TODO: Investigate timestamp discrepancy between Chat API and Monitoring API
 *
 * When viewing chat in monitoring mode, the timestamps shown in ChatPanel (from Chat API)
 * differ from those in LogsPanel (from Monitoring API). For example:
 * - Chat panel shows "02:57 PM" for user message
 * - Execution log shows "06:56 AM" for the same message
 *
 * This appears to be a backend issue where the two APIs record/return different timestamps.
 * Need to investigate on the server side whether this is:
 * 1. Different clocks/timestamps between APIs
 * 2. Different events being recorded (message received vs workflow action started)
 * 3. Timezone handling differences
 */

type ChatPanelProps = {
  agentUrl?: string | undefined;
  apiKey?: string | undefined;
  developerKey?: string | undefined;
  disabled?: boolean | undefined;
  /** Context ID (run ID) to connect to an existing conversation */
  contextId?: string | undefined;
  /** Whether the panel is collapsed */
  isCollapsed?: boolean;
  /** Called when collapse state should change */
  onCollapseChange?: (collapsed: boolean) => void;
  /** Called when user clicks New Chat - parent handles starting new run */
  onNewChat?: (() => void) | undefined;
  /** Whether a new chat is being created (shows loading state) */
  isCreatingNewChat?: boolean | undefined;
  /** Called when a new run is created from sending the first message (returns contextId/runId) */
  onRunCreated?: ((runId: string) => void) | undefined;
  /** Called when user clicks "Load previous sessions" - opens run history panel */
  onOpenRunHistory?: (() => void) | undefined;
  /** Whether to auto-focus the input field */
  autoFocus?: boolean | undefined;
  /** Whether to hide the header action buttons (new chat, history) */
  hideHeaderActions?: boolean | undefined;
};

const COLLAPSED_WIDTH = 32;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    flex: 1,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  // Collapsed state - thin vertical bar
  collapsedContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: `${COLLAPSED_WIDTH}px`,
    minWidth: `${COLLAPSED_WIDTH}px`,
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingTop: tokens.spacingVerticalS,
    gap: tokens.spacingVerticalS,
  },
  collapsedIcon: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase400,
  },
  collapsedTitle: {
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
    flex: 1,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expandButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },
  collapseButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    padding: tokens.spacingHorizontalL,
  },
  emptyIcon: {
    fontSize: '48px',
    color: tokens.colorBrandForeground1,
  },
  emptyStateTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground2,
  },
  emptyStateText: {
    fontSize: tokens.fontSizeBase200,
  },
  inputContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  input: {
    flex: 1,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
  },
  sessionMenuItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  sessionName: {
    fontWeight: tokens.fontWeightMedium,
  },
  sessionPreview: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '200px',
  },
  notConfigured: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    padding: tokens.spacingHorizontalL,
  },
  errorBar: {
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

export const ChatPanel = ({
  agentUrl,
  apiKey,
  developerKey,
  disabled = false,
  contextId,
  isCollapsed = false,
  onCollapseChange,
  onNewChat,
  isCreatingNewChat = false,
  onRunCreated,
  onOpenRunHistory,
  autoFocus = false,
  hideHeaderActions = false,
}: ChatPanelProps) => {
  const styles = useStyles();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus the input when autoFocus is true
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Only initialize chat if we have the agent URL
  const isConfigured = !!agentUrl;

  const {
    messages,
    sessions,
    currentSessionId,
    isStreaming,
    isLoading,
    error,
    sendMessage,
    createNewSession,
    switchSession,
    loadSessions,
    cancelStream,
    clearError,
  } = useAgentChat({
    agentUrl: agentUrl || '',
    apiKey,
    developerKey,
    autoLoadSessions: isConfigured,
  });

  // Auto-load context when contextId is provided (for monitoring mode)
  // This connects the chat to an existing run/conversation
  // When contextId changes, always switch to that session to load its messages
  useEffect(() => {
    if (contextId && isConfigured) {
      switchSession(contextId);
    }
  }, [contextId, isConfigured, switchSession]);

  // Convert messages to ChatMessageData format
  const chatMessages = useMemo((): ChatMessageData[] => {
    return messages.map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
      isStreaming: msg.isStreaming,
    }));
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, isStreaming]);

  // Track previous session ID to detect when a new run is created
  const prevSessionIdRef = useRef<string | null>(null);

  // Notify parent when a new run is created (currentSessionId changes from null to a value)
  // This happens when the first message is sent and the server creates a new context/run
  useEffect(() => {
    // Only fire if:
    // 1. We have a callback
    // 2. We weren't given a contextId prop (meaning we're starting a new chat)
    // 3. Previous sessionId was null (no existing session)
    // 4. Current sessionId is now set (new session created)
    if (onRunCreated && !contextId && prevSessionIdRef.current === null && currentSessionId) {
      onRunCreated(currentSessionId);
    }
    prevSessionIdRef.current = currentSessionId;
  }, [currentSessionId, contextId, onRunCreated]);

  const handleSend = async () => {
    if (inputValue.trim() && !disabled && !isStreaming) {
      const message = inputValue.trim();
      setInputValue('');
      await sendMessage(message);
      // Refocus input after sending
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCancel = () => {
    cancelStream();
  };

  const formatSessionName = (session: { id: string; name?: string | undefined; createdAt: Date }) => {
    if (session.name) return session.name;
    return `Session ${formatDate(session.createdAt)}`;
  };

  // Collapsed state - show thin vertical bar
  if (isCollapsed) {
    return (
      <div className={styles.collapsedContainer}>
        <Tooltip content="Expand Chat" relationship="label" positioning="after">
          <Button
            className={styles.expandButton}
            appearance="subtle"
            size="small"
            icon={<ChevronRightRegular />}
            onClick={() => onCollapseChange?.(false)}
            aria-label="Expand Chat"
          />
        </Tooltip>
        <ChatRegular className={styles.collapsedIcon} />
        <Text className={styles.collapsedTitle}>Chat</Text>
      </div>
    );
  }

  // Show not configured state if no agent URL
  if (!isConfigured) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Text className={styles.headerTitle}>Chat</Text>
          </div>
          {onCollapseChange && (
            <Tooltip content="Collapse" relationship="label">
              <Button
                className={styles.collapseButton}
                appearance="subtle"
                size="small"
                icon={<ChevronLeftRegular />}
                onClick={() => onCollapseChange(true)}
                aria-label="Collapse Chat"
              />
            </Tooltip>
          )}
        </div>
        <div className={styles.notConfigured}>
          <Text className={styles.emptyStateTitle}>Chat Not Available</Text>
          <Text className={styles.emptyStateText}>
            Unable to fetch all the details. Chat is not available.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Text className={styles.headerTitle}>Chat</Text>
          {currentSessionId && (
            <Badge appearance="ghost" size="small">
              Session Active
            </Badge>
          )}
        </div>
        <div className={styles.headerActions}>
          {!hideHeaderActions && (
            <Tooltip content="New Chat" relationship="label">
              <Button
                icon={<AddFilled />}
                appearance="subtle"
                size="small"
                onClick={onNewChat ?? createNewSession}
                disabled={disabled || isCreatingNewChat}
                aria-label="Start new chat"
              />
            </Tooltip>
          )}
          {/* Hide history button in monitoring mode (when contextId is provided) or when hideHeaderActions is true */}
          {!contextId && !hideHeaderActions && (
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Tooltip content="Chat History" relationship="label">
                  <Button
                    icon={<HistoryFilled />}
                    appearance="subtle"
                    size="small"
                    disabled={disabled || sessions.length === 0}
                    aria-label="View chat history"
                  />
                </Tooltip>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {sessions.length === 0 ? (
                    <MenuItem disabled>No previous sessions</MenuItem>
                  ) : (
                    sessions.slice(0, 10).map((session) => (
                      <MenuItem
                        key={session.id}
                        onClick={() => switchSession(session.id)}
                      >
                        <div className={styles.sessionMenuItem}>
                          <span className={styles.sessionName}>
                            {formatSessionName(session)}
                          </span>
                          {session.lastMessage && (
                            <span className={styles.sessionPreview}>
                              {session.lastMessage}
                            </span>
                          )}
                        </div>
                      </MenuItem>
                    ))
                  )}
                  {sessions.length > 10 && (
                    <MenuItem disabled>
                      +{sessions.length - 10} more sessions
                    </MenuItem>
                  )}
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
          {onCollapseChange && (
            <Tooltip content="Collapse" relationship="label">
              <Button
                className={styles.collapseButton}
                appearance="subtle"
                size="small"
                icon={<ChevronLeftRegular />}
                onClick={() => onCollapseChange(true)}
                aria-label="Collapse Chat"
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div
        className={styles.messagesContainer}
        role="list"
        aria-label="Chat messages"
      >
        {isLoading && chatMessages.length === 0 ? (
          <div className={styles.loadingContainer}>
            <Spinner size="small" label="Loading messages..." />
          </div>
        ) : chatMessages.length === 0 ? (
          <div className={styles.emptyState}>
            <BotSparkleRegular className={styles.emptyIcon} />
            <Text className={styles.emptyStateTitle}>Chat with Otto</Text>
            <Text className={styles.emptyStateText}>
              Send a message to begin chatting with the agent workflow.
            </Text>
            <Button
              appearance="subtle"
              icon={<HistoryFilled />}
              onClick={onOpenRunHistory ?? loadSessions}
            >
              Load previous sessions
            </Button>
          </div>
        ) : (
          <>
            {chatMessages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {error && (
        <div className={styles.errorBar}>
          <Text size={200}>{error}</Text>
          <Button
            icon={<DismissFilled />}
            appearance="subtle"
            size="small"
            onClick={clearError}
            aria-label="Dismiss error"
          />
        </div>
      )}

      <div className={styles.inputContainer}>
        <Input
          input={{ ref: inputRef }}
          className={styles.input}
          placeholder={isStreaming ? 'Agent is responding...' : 'Type a message...'}
          value={inputValue}
          onChange={(_, data) => setInputValue(data.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || isStreaming}
          aria-label="Message input"
        />
        {isStreaming ? (
          <Tooltip content="Cancel response" relationship="label">
            <Button
              icon={<DismissFilled />}
              appearance="secondary"
              onClick={handleCancel}
              aria-label="Cancel response"
            />
          </Tooltip>
        ) : (
          <Button
            icon={<SendFilled />}
            appearance="primary"
            onClick={handleSend}
            disabled={!inputValue.trim() || disabled}
            aria-label="Send message"
          />
        )}
      </div>
    </div>
  );
};
